import Edit2D = Autodesk.Extensions.Edit2D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;
import Viewer3D = Autodesk.Viewing.Viewer3D;
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';
import {
  SheetShapeDeepFragment,
  ShapeFolderDeepFragment,
} from 'src/gql/graphql';
import { getSheetShapeUrn } from 'src/components/viewers/common/hooks/useSheetShapesManager';

export interface ISheetShape {
  readonly apiModel?: SheetShapeDeepFragment | null | undefined;

  get urn(): string;

  get dbId(): number;

  get selected(): boolean;

  draw(): void;

  remove(): void;

  hitTest(x: number, y: number): boolean;

  setSelected(selected: boolean): void;

  setHovered(hovered: boolean): void;

  getLabels(options?: {
    fontSize?: number;
    shapeFolders?: ShapeFolderDeepFragment[];
    isProtan?: boolean;
  }): {
    point: THREE.Vector3;
    label: string;
    rotation?: number;
    fontSize?: number;
  }[];
}

export enum SheetShapeStyleType {
  default = 'default',
  hovered = 'hovered',
  selected = 'selected',
}

export abstract class AbstractSheetShape implements ISheetShape {
  protected isHovered = false;
  protected isSelected = false;
  protected textLabelPoints: { x: number; y: number; segmentIndex?: number }[] =
    [];
  protected shapes: Autodesk.Edit2D.Shape[] = [];

  protected constructor(
    protected readonly viewer: Viewer3D | GuiViewer3D,
    protected readonly edit2d: Edit2D,
    protected readonly activeSheetData: ActiveSheetData,
    public readonly apiModel?: SheetShapeDeepFragment
  ) {}

  get urn() {
    return (
      this.apiModel?.urn || getSheetShapeUrn(this.activeSheetData.projectId)
    );
  }

  get dbId() {
    return this.apiModel?.dbId || 0;
  }

  get selected() {
    return this.isSelected;
  }

  public hitTest(x: number, y: number) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.shapes.some((shape) => shape.hitTest(x, y));
  }

  public draw(): void {
    this.edit2d.defaultContext.layer.addShapes(this.shapes);
  }

  public remove(): void {
    this.edit2d.defaultContext.layer.removeShapes(this.shapes);
  }

  public setSelected(selected = true) {
    this.isSelected = selected;

    if (selected) {
      this.setStyle(SheetShapeStyleType.selected);
    } else {
      this.setStyle(SheetShapeStyleType.default);
    }

    // Use layer.update() for fast style updates (as shown in Autodesk docs)
    this.updateLayer();
  }

  public setHovered(hovered = true) {
    this.isHovered = hovered;

    if (this.isSelected) {
      return;
    } else if (hovered) {
      this.setStyle(SheetShapeStyleType.hovered);
    } else {
      this.setStyle(SheetShapeStyleType.default);
    }

    // Use layer.update() for fast style updates (as shown in Autodesk docs)
    this.updateLayer();
  }

  // Minimal method to update the layer after style changes
  private updateLayer(): void {
    if (this.edit2d.defaultContext && this.edit2d.defaultContext.layer) {
      const layer = this.edit2d.defaultContext.layer;
      if (typeof (layer as any).update === 'function') {
        (layer as any).update();
      }
    }
  }

  public getLabels(options?: {
    fontSize?: number;
    shapeFolders?: ShapeFolderDeepFragment[];
    isProtan?: boolean;
  }) {
    return this.textLabelPoints.map((point) => {
      let label = this.apiModel?.name || '';

      // For Protan tenants, show folder name instead of shape name for polygon shapes
      if (
        options?.isProtan &&
        options?.shapeFolders &&
        this.apiModel?.sheetShapePolygon
      ) {
        const folderName = this.getShapeFolderName(
          this.apiModel,
          options.shapeFolders
        );
        if (folderName) {
          label = folderName;
        }
      }

      return {
        point: this.viewer.worldToClient(
          new THREE.Vector3(point.x, point.y, 0)
        ),
        label,
        fontSize: options?.fontSize,
        rotation: undefined,
      };
    });
  }

  private getShapeFolderName(
    shape: SheetShapeDeepFragment,
    folders: ShapeFolderDeepFragment[]
  ): string | undefined {
    if (shape.autoShapeId) {
      return folders.find((folder) => folder.id === shape.autoShapeId)?.name;
    }
    return folders.find((folder) => folder.id === shape.folder)?.name;
  }

  protected convertPointToRender(apiPoint: { x: number; y: number }) {
    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );
    const converted = converter.convertToAutodeskPoint(apiPoint);

    return { x: converted.x, y: converted.y };
  }

  protected setStyle(type: SheetShapeStyleType) {
    throw new Error(`Unimplemented method .setStyle()`);
  }
}
